import type { PaymentQueueParams, PaymentSummaryParams } from "../types/payment";

export const PAYMENT_BASE_URL =
  "/fulfilment/accounts-payable/api/v1/vip/payment-processing";

export const VIP_VENDOR_FACILITY_BASE =
  "/fulfilment/accounts-payable/api/v1/vip/vendor-facility";

export const paymentKeys = {
  all: ["v2-payments"] as const,
  lists: () => [...paymentKeys.all, "list"] as const,
  list: (params: PaymentQueueParams) => [...paymentKeys.lists(), params] as const,
  details: () => [...paymentKeys.all, "detail"] as const,
  detail: (id: number) => [...paymentKeys.details(), id] as const,
  batchable: () => [...paymentKeys.all, "batchable"] as const,
  summary: (params: PaymentSummaryParams) => [...paymentKeys.all, "summary", params] as const,
};

/**
 * Separate root for factory→facility ID lookups.
 *
 * Kept outside paymentKeys.all deliberately: approve/reject/upload mutations
 * call queryClient.invalidateQueries(paymentKeys.all) to refresh the queue and
 * summary. The facility mapping is stable (TTL-cached for 5 min) and should
 * NOT be refetched on every payment action, so nesting it here would cause
 * unnecessary network calls.
 */
export const facilityKeys = {
  all: ["v2-facility-ids"] as const,
  ids: (factoryCode: string) => [...facilityKeys.all, factoryCode] as const,
};

export const walletKeys = {
  all: ["v2-wallet"] as const,
  balance: (facilityId: number) => [...walletKeys.all, "balance", facilityId] as const,
  facilityDetails: (factoryCode: string) =>
    [...walletKeys.all, "facility-details", factoryCode] as const,
  // Gringotts-backed ledger reads, keyed by walletId (not facilityId) because
  // that is what Gringotts addresses.
  summary: (walletId: number, range: unknown) =>
    [...walletKeys.all, "gringotts-summary", walletId, range] as const,
  transactions: (walletId: number, params: unknown) =>
    [...walletKeys.all, "gringotts-transactions", walletId, params] as const,
  // Open-advance entries (ENG-27316). Under walletKeys rather than paymentKeys
  // so a payment approve/reject (which invalidates paymentKeys.all) leaves it be.
  entriesAll: () => [...walletKeys.all, "entries"] as const,
  entries: (factoryCode: string) => [...walletKeys.entriesAll(), factoryCode] as const,
  // Paged settled entries — under entriesAll so a refund refetches them too.
  entryHistory: (factoryCode: string, states: string[]) =>
    [...walletKeys.entriesAll(), factoryCode, "history", states.join(",")] as const,
};

/** Gringotts through the gateway. VIP's transactions endpoint is deferred. */
export const GRINGOTTS_BASE_URL = "/gringotts/api/v1/wallets";
